import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import {
  STREAM_VIDEO_ACCEPT,
  STREAM_VIDEO_ERRORS,
  buildStreamThumbnailUrl,
  clampStreamPosterSeconds,
  defaultStreamPosterSeconds,
  isStreamUid,
  uploadStreamVideo,
  withStreamPoster,
  type StreamVideoStatus,
} from '../utils/streamVideo';
import { discardStreamVideo } from '../utils/streamVideoLifecycle';
import { hasAdvancedComposerPermission } from '../utils/assignMenu';

type StreamVideoUploadButtonProps = {
  onInsert: (iframeSrc: string, uid?: string) => void;
  onProcessing?: (status: StreamVideoStatus) => void;
  onRemove?: (uid?: string) => void;
  onRestore?: () => void;
  onPosterChange?: (uid: string, seconds: number) => void;
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
};

type UploadPhase = 'idle' | 'selected' | 'uploading' | 'processing' | 'ready' | 'error';

function assertNever(value: never): never {
  throw new Error(`Unsupported Stream upload phase: ${String(value)}`);
}

export function StreamVideoUploadButton({
  onInsert,
  onProcessing,
  onRemove,
  onRestore,
  onPosterChange,
  onBusyChange,
  disabled = false,
}: StreamVideoUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const uidRef = useRef('');
  // attachedUidRef is only written from this session's mint / upload success
  // (onMinted / onProcessing / result.uid). Never hydrate it from existing
  // post HTML — a saved iframe uid is already posted and must not be discarded.
  const attachedUidRef = useRef('');
  const attemptUidRef = useRef('');
  const replacedUidRef = useRef('');
  const onInsertRef = useRef(onInsert);
  const onProcessingRef = useRef(onProcessing);
  const onRemoveRef = useRef(onRemove);
  const onRestoreRef = useRef(onRestore);
  const onBusyChangeRef = useRef(onBusyChange);
  const disabledRef = useRef(disabled);
  const aliveRef = useRef(true);
  const inFlightRef = useRef(false);
  onInsertRef.current = onInsert;
  onProcessingRef.current = onProcessing;
  onRemoveRef.current = onRemove;
  onRestoreRef.current = onRestore;
  onBusyChangeRef.current = onBusyChange;
  disabledRef.current = disabled;
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [readyUid, setReadyUid] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [posterSeconds, setPosterSeconds] = useState(0);
  const posterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPosterChangeRef = useRef(onPosterChange);
  onPosterChangeRef.current = onPosterChange;

  useEffect(
    () => () => {
      if (posterTimerRef.current) clearTimeout(posterTimerRef.current);
    },
    []
  );

  const onPosterInput = (event: ChangeEvent<HTMLInputElement>) => {
    const uid = readyUid;
    const seconds = clampStreamPosterSeconds(Number(event.target.value), durationSeconds);
    setPosterSeconds(seconds);
    if (posterTimerRef.current) clearTimeout(posterTimerRef.current);
    // Rewriting the editor embed on every slider tick would thrash Quill.
    posterTimerRef.current = setTimeout(() => {
      posterTimerRef.current = null;
      if (aliveRef.current && isStreamUid(uid)) onPosterChangeRef.current?.(uid, seconds);
    }, 250);
  };

  const discardUid = (uid: string) => {
    if (disabledRef.current) return;
    if (isStreamUid(uid)) {
      void discardStreamVideo(uid);
    }
  };

  const clearEditorMarkup = () => {
    onRemoveRef.current?.(uidRef.current || undefined);
  };

  const restoreEditorMarkup = () => {
    if (onRestoreRef.current) {
      onRestoreRef.current();
      return;
    }
    onRemoveRef.current?.(uidRef.current || undefined);
  };

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      // Do not abort the in-flight mint/TUS/XHR. A streamBusy parent
      // re-render (and React Strict Mode in development) remounts this
      // button; aborting leaves Stream at pendingupload with no bytes.
      // Cancel is explicit via the Cancel button or a replacement pick.
      // While that XHR is still going, do not restore the prior player or
      // clear busy — the parent stays locked until this attempt settles.
      // The completing instance may still insert into the stable parent bind.
      if (inFlightRef.current) {
        return;
      }
      if (onRestoreRef.current) {
        onRestoreRef.current();
      } else {
        onRemoveRef.current?.(uidRef.current || undefined);
      }
      onBusyChangeRef.current?.(false);
    };
  }, []);

  const onPickClick = () => {
    if (disabled || phase === 'uploading' || phase === 'processing') return;
    inputRef.current?.click();
  };

  const onCancelClick = () => {
    if (disabledRef.current) return;
    abortRef.current?.abort();
    restoreEditorMarkup();
  };

  const onClearClick = () => {
    if (disabledRef.current) return;
    discardUid(attachedUidRef.current);
    if (attemptUidRef.current && attemptUidRef.current !== attachedUidRef.current) {
      discardUid(attemptUidRef.current);
    }
    clearEditorMarkup();
    uidRef.current = '';
    attachedUidRef.current = '';
    attemptUidRef.current = '';
    replacedUidRef.current = '';
    setPhase('idle');
    setProgress(0);
    setFileName('');
    setErrorMessage('');
    setReadyUid('');
    onBusyChange?.(false);
  };

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;
    replacedUidRef.current = attachedUidRef.current;
    attemptUidRef.current = '';
    uidRef.current = '';
    setFileName(file.name);
    setErrorMessage('');
    setReadyUid('');
    setProgress(1);
    setPhase('uploading');
    onBusyChangeRef.current?.(true);
    const settleStaleAttempt = () => {
      if (attemptUidRef.current && attemptUidRef.current !== replacedUidRef.current) {
        discardUid(attemptUidRef.current);
      }
    };
    try {
      const result = await uploadStreamVideo(file, {
        onProgress: (percent) => {
          if (aliveRef.current) setProgress(percent);
        },
        onMinted: (uid) => {
          attemptUidRef.current = uid;
          uidRef.current = uid;
        },
        onProcessing: (status) => {
          attemptUidRef.current = status.uid;
          uidRef.current = status.uid;
          if (!aliveRef.current) return;
          setPhase('processing');
          onProcessingRef.current?.(status);
        },
        signal: controller.signal,
      });
      const firstPoster = defaultStreamPosterSeconds(result.durationSeconds);
      const posterSrc = withStreamPoster(result.iframeSrc, firstPoster);
      if (!aliveRef.current) {
        if (!controller.signal.aborted) {
          onInsertRef.current(posterSrc, result.uid);
        } else {
          settleStaleAttempt();
        }
        return;
      }
      if (!controller.signal.aborted) {
        if (replacedUidRef.current && replacedUidRef.current !== result.uid) {
          discardUid(replacedUidRef.current);
        }
        uidRef.current = result.uid;
        attachedUidRef.current = result.uid;
        attemptUidRef.current = result.uid;
        replacedUidRef.current = '';
        onInsertRef.current(posterSrc, result.uid);
        setReadyUid(result.uid);
        setDurationSeconds(result.durationSeconds);
        setPosterSeconds(firstPoster);
        setProgress(100);
        setPhase('ready');
      } else if (abortRef.current === controller) {
        if (attemptUidRef.current && attemptUidRef.current !== replacedUidRef.current) {
          discardUid(attemptUidRef.current);
        }
        attachedUidRef.current = replacedUidRef.current;
        restoreEditorMarkup();
      }
    } catch (error) {
      if (!aliveRef.current) {
        settleStaleAttempt();
        return;
      }
      if (attemptUidRef.current && attemptUidRef.current !== replacedUidRef.current) {
        discardUid(attemptUidRef.current);
      }
      attachedUidRef.current = replacedUidRef.current;
      if (abortRef.current === controller || !abortRef.current) {
        restoreEditorMarkup();
      }
      if (controller.signal.aborted) {
        setPhase('idle');
        setProgress(0);
        setFileName('');
        return;
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : STREAM_VIDEO_ERRORS.uploadFailed;
      if (message === STREAM_VIDEO_ERRORS.cancelled) {
        setPhase('idle');
        setProgress(0);
        return;
      }
      setErrorMessage(message);
      setPhase('error');
      toast.error(message);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        inFlightRef.current = false;
        onBusyChangeRef.current?.(false);
      }
    }
  };

  const statusText = (() => {
    switch (phase) {
      case 'idle':
        return '';
      case 'selected':
        return `${fileName} selected`;
      case 'uploading':
        return `Uploading ${fileName || 'video'}… ${progress}%`;
      case 'processing':
        return `Processing ${fileName || 'video'}…`;
      case 'ready':
        return `${fileName || 'Video'} attached and ready to post`;
      case 'error':
        return errorMessage;
      default:
        return assertNever(phase);
    }
  })();

  const buttonLabel = (() => {
    switch (phase) {
      case 'uploading':
        return 'Uploading video…';
      case 'processing':
        return 'Processing video…';
      case 'ready':
        return 'Replace video';
      case 'error':
        return 'Try another video';
      case 'idle':
      case 'selected':
        return 'Upload video';
      default:
        return assertNever(phase);
    }
  })();

  if (!hasAdvancedComposerPermission()) {
    return null;
  }

  return (
    <div className={`mw-stream-upload mw-stream-upload--${phase}`}>
      <input
        ref={inputRef}
        className="mw-stream-upload-input"
        type="file"
        accept={STREAM_VIDEO_ACCEPT}
        aria-label="Upload video"
        disabled={disabled || phase === 'uploading' || phase === 'processing'}
        onChange={onFileChange}
      />
      <button
        type="button"
        className="mw-stream-upload-button"
        disabled={disabled || phase === 'uploading' || phase === 'processing'}
        onClick={onPickClick}
      >
        {buttonLabel}
      </button>
      {phase === 'uploading' || phase === 'processing' ? (
        <button
          type="button"
          className="mw-stream-upload-cancel"
          disabled={disabled}
          onClick={onCancelClick}
        >
          Cancel
        </button>
      ) : null}
      {phase === 'ready' || phase === 'error' ? (
        <button
          type="button"
          className="mw-stream-upload-clear"
          disabled={disabled}
          onClick={onClearClick}
        >
          Clear
        </button>
      ) : null}
      {fileName && phase !== 'idle' ? (
        <p className="mw-stream-upload-filename">{fileName}</p>
      ) : null}
      {phase === 'uploading' ? (
        <div
          className="mw-stream-upload-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <span
            className="mw-stream-upload-progress-bar"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
          <span className="mw-stream-upload-progress-label">{progress}%</span>
        </div>
      ) : null}
      {phase === 'processing' ? (
        <p className="mw-stream-upload-processing" role="status">
          Processing video…
        </p>
      ) : null}
      {phase === 'ready' ? (
        <p className="mw-stream-upload-ready" role="status">
          Video attached and ready to post
        </p>
      ) : null}
      {phase === 'ready' && readyUid && onPosterChange ? (
        <div className="mw-stream-poster-picker">
          <label className="mw-stream-poster-label" htmlFor={`mw-stream-poster-${readyUid}`}>
            Choose the frame shown in the Lounge
          </label>
          <img
            className="mw-stream-poster-preview"
            src={buildStreamThumbnailUrl(readyUid, posterSeconds, { height: 270 })}
            alt={`Video frame at ${posterSeconds.toFixed(1)} seconds`}
          />
          <input
            id={`mw-stream-poster-${readyUid}`}
            className="mw-stream-poster-range"
            type="range"
            min={0}
            max={Math.max(0, clampStreamPosterSeconds(durationSeconds, durationSeconds))}
            step={0.1}
            value={posterSeconds}
            disabled={disabled}
            onChange={onPosterInput}
          />
          <span className="mw-stream-poster-time">{posterSeconds.toFixed(1)}s</span>
        </div>
      ) : null}
      {phase === 'error' && errorMessage ? (
        <p className="mw-stream-upload-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <p className="mw-stream-upload-sr-status" aria-live="polite">
        {statusText}
      </p>
    </div>
  );
}
